import React, { useMemo, useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Link, Pill, Text, Tooltip } from "@foris/avocado-suite";
import { Step } from "react-joyride";
import TourTooltip from "@common/components/tour-tooltip";
import {
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";
import styles from "./groupTableCourseColumn.module.scss";

interface GroupTableCourseColumnProps {
  groupLabel: string;
  curriculumCode?: string;
  programCode?: string;
  departmentCode?: string;
  modalityCode?: string;
  shiftCode?: string;
  termCode?: string;
  campusCode?: string;
  hasOnboarding?: boolean;
}

/**
 * Component that renders a table column for displaying group course information.
 *
 * @component
 * @param {GroupTableCourseColumnProps} props - The props for the component.
 * @param {string} props.groupLabel - The label of the group.
 * @param {string} props.curriculumCode - The code of the curriculum.
 * @param {string} props.programCode - The code of the program.
 * @param {string} props.departmentCode - The code of the department.
 * @param {string} props.modalityCode - The code of the modality.
 * @param {string} props.shiftCode - The code of the shift.
 * @param {string} props.termCode - The code of the term.
 * @param {string} props.campusCode - The code of the campus.
 *
 * @returns {JSX.Element} The rendered component.
 */
const GroupTableCourseColumn = ({
  groupLabel,
  curriculumCode,
  programCode,
  departmentCode,
  modalityCode,
  shiftCode,
  termCode,
  campusCode,
  hasOnboarding = true,
}: GroupTableCourseColumnProps) => {
  const { t } = useTranslation();
  const [showPills, setShowpills] = useState(false);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);

  const showHotspot = useMemo(() => {
    return hasOnboarding && shouldShowHotspot("bundle-subject-key");
  }, [onboardingsSeen, hasOnboarding]);

  const steps: Step[] = [
    {
      content: t("onboarding.bundle-subject-key.description"),
      target: ".courseColumnTour",
      locale: {
        last: t("onboarding.actions.done"),
      },
      title: t("onboarding.bundle-subject-key.title"),
      disableBeacon: false,
      placement: "bottom",
      placementBeacon: "left",
    },
  ];

  return (
    <>
      <div className={cx(styles.groupTableCourseColumn, "courseColumnTour")}>
        <Tooltip label={groupLabel} placement="top">
          <Text className={styles.groupTableCourseColumn_code}>{groupLabel}</Text>
        </Tooltip>

        <div className={styles.groupTableCourseColumn_pills}>
          <Pill className={styles.coursePill} outlined>
            {t("bundle.link-card.group-table.body.subject-key.term")}: {termCode ?? "-"}
          </Pill>
          <Pill className={styles.coursePill} outlined>
            {t("bundle.link-card.group-table.body.subject-key.campus")}: {campusCode ?? "-"}
          </Pill>

          {showPills && (
            <>
              <Pill className={styles.coursePill} outlined>
                {t("bundle.link-card.group-table.body.subject-key.department")}:{" "}
                {departmentCode ?? "-"}
              </Pill>
              <Pill className={styles.coursePill} outlined>
                {t("bundle.link-card.group-table.body.subject-key.modality")}: {modalityCode ?? "-"}
              </Pill>
              <Pill className={styles.coursePill} outlined>
                {t("bundle.link-card.group-table.body.subject-key.shift")}: {shiftCode ?? "-"}
              </Pill>
              <Pill className={styles.coursePill} outlined>
                {t("bundle.link-card.group-table.body.subject-key.program")}: {programCode ?? "-"}
              </Pill>
              <Pill className={styles.coursePill} outlined>
                {t("bundle.link-card.group-table.body.subject-key.curriculum")}:{" "}
                {curriculumCode ?? "-"}
              </Pill>
            </>
          )}

          <Link
            iconRight={!showPills ? "chevron-down" : "chevron-up"}
            size="sm"
            onClick={() => setShowpills(!showPills)}
          >
            {showPills
              ? t("bundle.link-card.group-table.body.subject-key.less")
              : t("bundle.link-card.group-table.body.subject-key.more")}
          </Link>
        </div>
      </div>

      {showHotspot && (
        <TourTooltip
          beaconClassName={styles.tourBeacon}
          startTour={showHotspot}
          steps={steps}
          onClose={() => setIsOnboardingOpen(false, "bundle-subject-key")}
          onToolipOpen={() => setIsOnboardingOpen(true, "bundle-subject-key")}
        />
      )}
    </>
  );
};

export default GroupTableCourseColumn;
